import { useMemo } from 'react'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import type { TimeSeriesPoint } from '@/types/timeseries'
import { CHART_BRAND } from '@/lib/chartColors'

interface MetricChartProps {
  type: 'area' | 'bar' | 'line'
  title: string
  data: TimeSeriesPoint[]
  color?: string
  height?: number
  valueSuffix?: string
  valueFormatter?: (n: number) => string
  loading?: boolean
}

const GRID_STROKE = 'hsl(var(--chart-grid))'
const AXIS_TICK = { fill: 'hsl(var(--chart-axis))', fontSize: 11 }
const ACTIVE_DOT_STROKE = 'hsl(var(--background))'

const CustomTooltip = ({
  active,
  payload,
  label,
  valueSuffix = '',
  valueFormatter,
}: {
  active?: boolean
  payload?: Array<{ value: number }>
  label?: string
  valueSuffix?: string
  valueFormatter?: (n: number) => string
}) => {
  if (!active || !payload || payload.length === 0) return null
  const value = payload[0].value
  const formatted = valueFormatter
    ? valueFormatter(value)
    : value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      })

  return (
    <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-semibold text-foreground">
        {formatted}{valueSuffix}
      </p>
    </div>
  )
}

function formatYAxis(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`
  if (value < 1 && value > 0) return value.toFixed(2)
  return value.toFixed(0)
}

function LoadingSkeleton({ height }: { height: number }) {
  return (
    <div className="flex items-end gap-1 px-4" style={{ height }}>
      {Array.from({ length: 12 }).map((_, i) => (
        <div
          key={i}
          className="flex-1 bg-muted rounded-t animate-pulse"
          style={{ height: `${20 + Math.random() * 60}%` }}
        />
      ))}
    </div>
  )
}

export function MetricChart({
  type,
  title,
  data,
  color = CHART_BRAND,
  height = 240,
  valueSuffix = '',
  valueFormatter,
  loading = false,
}: MetricChartProps) {
  const gradientId = useMemo(
    () => `metric-${type}-${Math.random().toString(36).slice(2, 9)}`,
    [type]
  )

  const isEmpty = !data.length || data.every((d) => d.value === 0)

  const yDomain = useMemo(() => {
    if (isEmpty) return [0, 10]
    const values = data.map((d) => d.value)
    const max = Math.max(...values)
    const padding = max * 0.2 || 10
    return [0, max + padding]
  }, [data, isEmpty])

  return (
    <div className="rounded-lg border border-border bg-card p-4 sm:p-6 shadow-sm">
      <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-3">
        {title}
      </p>

      {loading ? (
        <LoadingSkeleton height={height} />
      ) : isEmpty ? (
        <div
          className="flex items-center justify-center text-muted-foreground text-sm"
          style={{ height }}
        >
          No data for this period
        </div>
      ) : type === 'area' ? (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="50%" stopColor={color} stopOpacity={0.15} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={AXIS_TICK}
              dy={8}
            />
            <YAxis
              domain={yDomain}
              axisLine={false}
              tickLine={false}
              tick={AXIS_TICK}
              tickFormatter={formatYAxis}
              width={50}
            />
            <Tooltip
              content={
                <CustomTooltip valueSuffix={valueSuffix} valueFormatter={valueFormatter} />
              }
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ fill: color, stroke: ACTIVE_DOT_STROKE, strokeWidth: 2, r: 5 }}
              animationDuration={500}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : type === 'bar' ? (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.9} />
                <stop offset="100%" stopColor={color} stopOpacity={0.3} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={AXIS_TICK}
              dy={8}
            />
            <YAxis
              domain={yDomain}
              axisLine={false}
              tickLine={false}
              tick={AXIS_TICK}
              tickFormatter={formatYAxis}
              width={50}
            />
            <Tooltip
              content={
                <CustomTooltip valueSuffix={valueSuffix} valueFormatter={valueFormatter} />
              }
              cursor={{ fill: GRID_STROKE }}
            />
            <Bar
              dataKey="value"
              fill={`url(#${gradientId})`}
              stroke={color}
              strokeWidth={1}
              maxBarSize={60}
              radius={[4, 4, 0, 0]}
              animationDuration={500}
              animationEasing="ease-out"
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={AXIS_TICK}
              dy={8}
            />
            <YAxis
              domain={yDomain}
              axisLine={false}
              tickLine={false}
              tick={AXIS_TICK}
              tickFormatter={formatYAxis}
              width={50}
            />
            <Tooltip
              content={
                <CustomTooltip valueSuffix={valueSuffix} valueFormatter={valueFormatter} />
              }
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={false}
              activeDot={{ fill: color, stroke: ACTIVE_DOT_STROKE, strokeWidth: 2, r: 5 }}
              animationDuration={500}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
